import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { deleteOrganization } from "@/lib/organization/api";
import {
  notificationSettingsSchema,
  updateProfileSchema,
  type NotificationSettingsFormData,
  type UpdateProfileFormData,
} from "@/lib/validations";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { AlertTriangle, Bell, Save, Shield, Trash2, User } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import GitHubApp from "../GitHubApp/GitHubApp";

export function SettingsPage() {
  const { user, signOut } = useAuth();
  const { toast } = useToast();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const deleteOrganizationMutation = useMutation({
    mutationFn: deleteOrganization,
    onSuccess: async () => {
      toast({
        title: "Account deleted",
        description: "Your account has been permanently deleted.",
        variant: "destructive",
      });
      await signOut();
    },
    onError: () => {
      console.error("Deletion failed. Sensitive details omitted for security.");
      toast({
        title: "Deletion failed",
        description: "Failed to delete your account. Please try again.",
        variant: "destructive",
      });
    },
  });

  const {
    register: registerProfile,
    formState: { errors: profileErrors },
  } = useForm<UpdateProfileFormData>({
    resolver: zodResolver(updateProfileSchema),
    defaultValues: {
      name: user?.displayName || "",
      email: user?.email || "",
    },
  });

  const {
    handleSubmit: handleSubmitNotifications,
    watch,
    setValue,
  } = useForm<NotificationSettingsFormData>({
    resolver: zodResolver(notificationSettingsSchema),
    defaultValues: {
      deployUpdates: true,
      securityAlerts: true,
      billing: true,
      marketing: false,
    },
  });

  const notificationValues = watch();

  const onSubmitNotifications = async (_: NotificationSettingsFormData) => {
    // Mock API call for notifications
    await new Promise((resolve) => setTimeout(resolve, 1000));

    toast({
      title: "Notification preferences updated",
      description: "Your notification settings have been saved.",
    });
  };

  const handleDeleteAccount = async () => {
    await deleteOrganizationMutation.mutateAsync();
    setIsDeleteModalOpen(false);
  };

  return (
    <div className="min-h-full w-full pb-4">
      <div className="p-4 md:p-6 lg:p-8 space-y-6 md:space-y-8">
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-cyan-400 mb-2">
            Settings
          </h1>
          <p className="text-sm md:text-base text-slate-300">
            Manage your account settings and preferences
          </p>
        </div>

        <Tabs defaultValue="profile" className="space-y-4 md:space-y-6 w-full">
          <TabsList className="bg-slate-800/50 border border-cyan-500/30 p-1 w-full md:w-auto flex flex-wrap md:flex-nowrap justify-start">
            <TabsTrigger
              value="profile"
              className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400 flex-1 md:flex-initial text-xs sm:text-sm"
            >
              Profile
            </TabsTrigger>
            {/* <TabsTrigger
              value="notifications" TODO bring back
              className=" data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400 ml-2"
            >
              Notifications
            </TabsTrigger> */}
            <TabsTrigger
              value="security"
              className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400 ml-1 md:ml-2 flex-1 md:flex-initial text-xs sm:text-sm"
            >
              Security
            </TabsTrigger>{" "}
            <TabsTrigger
              value="github"
              className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400 ml-1 md:ml-2 flex-1 md:flex-initial text-xs sm:text-sm"
            >
              GitHub
            </TabsTrigger>
            <TabsTrigger
              value="danger"
              className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400 ml-1 md:ml-2 flex-1 md:flex-initial text-xs sm:text-sm"
            >
              Danger Zone
            </TabsTrigger>
          </TabsList>
          <div className=""></div>
          <TabsContent value="profile" className="space-y-4 md:space-y-6">
            <Card className="holo-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-cyan-400 text-lg md:text-xl">
                  <User className="h-5 w-5" />
                  Profile Information
                </CardTitle>
                <CardDescription className="text-slate-400 text-sm"></CardDescription>
              </CardHeader>
              <CardContent>
                <form className="space-y-3 md:space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                    <div className="space-y-1.5 md:space-y-2">
                      <Label
                        htmlFor="name"
                        className="text-cyan-300 text-sm md:text-base"
                      >
                        Full Name
                      </Label>
                      <Input
                        id="name"
                        disabled
                        {...registerProfile("name")}
                        className="bg-slate-800/50 border-cyan-500/30 text-cyan-100 focus:border-cyan-400"
                      />
                      {profileErrors.name && (
                        <p className="text-red-400 text-xs">
                          {profileErrors.name.message}
                        </p>
                      )}
                    </div>
                    <div className="space-y-1.5 md:space-y-2">
                      <Label
                        htmlFor="email"
                        className="text-cyan-300 text-sm md:text-base"
                      >
                        Email
                      </Label>
                      <Input
                        disabled
                        id="email"
                        type="email"
                        {...registerProfile("email")}
                        className="bg-slate-800/50 border-cyan-500/30 text-cyan-100 focus:border-cyan-400"
                      />
                      {profileErrors.email && (
                        <p className="text-red-400 text-xs">
                          {profileErrors.email.message}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* <Button
                    type="submit"
                    disabled={isUpdatingProfile}
                    className="cyber-button "
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {isUpdatingProfile ? "SAVING..." : "SAVE CHANGES"}
                  </Button> */}
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="notifications" className="space-y-4 md:space-y-6">
            <Card className="holo-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-cyan-400 text-lg md:text-xl">
                  <Bell className="h-5 w-5" />
                  Notification Preferences
                </CardTitle>
                <CardDescription className="text-slate-400 text-sm">
                  Configure how you want to be notified about important events
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form
                  onSubmit={handleSubmitNotifications(onSubmitNotifications)}
                  className="space-y-4 md:space-y-6"
                >
                  <div className="space-y-3 md:space-y-4">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-cyan-100 text-sm md:text-base">
                          Deploy Updates
                        </h4>
                        <p className="text-xs md:text-sm text-slate-400">
                          Get notified when your deploys succeed or fail
                        </p>
                      </div>
                      <Switch
                        checked={notificationValues.deployUpdates}
                        onCheckedChange={(checked) =>
                          setValue("deployUpdates", checked)
                        }
                        className="self-start sm:self-auto"
                      />
                    </div>

                    <Separator className="bg-slate-700/50" />

                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-cyan-100 text-sm md:text-base">
                          Security Alerts
                        </h4>
                        <p className="text-xs md:text-sm text-slate-400">
                          Important security notifications and login alerts
                        </p>
                      </div>
                      <Switch
                        checked={notificationValues.securityAlerts}
                        onCheckedChange={(checked) =>
                          setValue("securityAlerts", checked)
                        }
                        className="self-start sm:self-auto"
                      />
                    </div>

                    <Separator className="bg-slate-700/50" />

                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-cyan-100 text-sm md:text-base">
                          Billing Notifications
                        </h4>
                        <p className="text-xs md:text-sm text-slate-400">
                          Payment confirmations and billing updates
                        </p>
                      </div>
                      <Switch
                        checked={notificationValues.billing}
                        onCheckedChange={(checked) =>
                          setValue("billing", checked)
                        }
                        className="self-start sm:self-auto"
                      />
                    </div>

                    <Separator className="bg-slate-700/50" />

                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-cyan-100 text-sm md:text-base">
                          Marketing Communications
                        </h4>
                        <p className="text-xs md:text-sm text-slate-400">
                          Product updates and promotional emails
                        </p>
                      </div>
                      <Switch
                        checked={notificationValues.marketing}
                        onCheckedChange={(checked) =>
                          setValue("marketing", checked)
                        }
                        className="self-start sm:self-auto"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="cyber-button w-full sm:w-auto"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    SAVE PREFERENCES
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="security" className="space-y-4 md:space-y-6">
            <Card className="holo-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-cyan-400 text-lg md:text-xl">
                  <Shield className="h-5 w-5" />
                  Security Settings
                </CardTitle>
                <CardDescription className="text-slate-400 text-sm">
                  Manage your account security and authentication
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 md:space-y-4">
                <div className="space-y-3 md:space-y-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-3 md:p-4 border border-cyan-500/30 rounded-lg bg-slate-800/30">
                    <div className="flex-1">
                      <h4 className="font-medium text-cyan-100 text-sm md:text-base">
                        GitHub Authentication
                      </h4>{" "}
                      <p className="text-xs md:text-sm text-slate-400">
                        Coming soon
                      </p>
                    </div>
                    <Button
                      disabled
                      variant="outline"
                      size="sm"
                      className="cyber-button w-full sm:w-auto"
                    >
                      Reconnect
                    </Button>
                  </div>

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-3 md:p-4 border border-cyan-500/30 rounded-lg bg-slate-800/30">
                    <div className="flex-1">
                      <h4 className="font-medium text-cyan-100 text-sm md:text-base">
                        Two-Factor Authentication
                      </h4>
                      <p className="text-xs md:text-sm text-slate-400">
                        Coming soon
                      </p>
                    </div>
                    <Button
                      disabled
                      variant="outline"
                      size="sm"
                      className="cyber-button w-full sm:w-auto"
                    >
                      Enable
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="github" className="space-y-4 md:space-y-6">
            <GitHubApp />
          </TabsContent>
          <TabsContent value="danger" className="space-y-4 md:space-y-6">
            <Card className="holo-card border-red-500/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-400 text-lg md:text-xl">
                  <Trash2 className="h-5 w-5" />
                  Danger Zone
                </CardTitle>
                <CardDescription className="text-slate-400 text-sm">
                  Irreversible and destructive actions
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 md:space-y-4">
                <div className="p-3 md:p-4 border border-red-500/30 rounded-lg bg-red-500/10">
                  <h4 className="font-medium text-red-400 mb-2 text-sm md:text-base">
                    Delete Account
                  </h4>
                  <p className="text-xs md:text-sm text-red-300 mb-3 md:mb-4">
                    Permanently delete your account and all associated data.
                    This action cannot be undone.
                  </p>

                  <Dialog
                    open={isDeleteModalOpen}
                    onOpenChange={setIsDeleteModalOpen}
                  >
                    <DialogTrigger asChild>
                      <Button
                        variant="destructive"
                        size="sm"
                        className="w-full sm:w-auto"
                      >
                        Delete Account
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-slate-900 border border-red-500/30">
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-400 text-base md:text-lg">
                          <AlertTriangle className="h-5 w-5" />
                          Confirm Account Deletion
                        </DialogTitle>
                        <DialogDescription className="text-slate-300 text-sm">
                          This action is irreversible and will permanently
                          delete your account and all associated data.
                        </DialogDescription>
                      </DialogHeader>

                      <div className="py-3 md:py-4">
                        <div className="p-3 md:p-4 border border-red-500/30 rounded-lg bg-red-500/10">
                          <h4 className="font-semibold text-red-400 mb-2 text-sm md:text-base">
                            What will be deleted:
                          </h4>
                          <ul className="text-xs md:text-sm text-red-300 space-y-1 list-disc list-inside">
                            <li>Your profile and account information</li>
                            <li>All projects and deployments</li>
                            <li>Settings and preferences</li>
                            <li>All associated data</li>
                          </ul>
                        </div>

                        <p className="text-xs md:text-sm text-slate-400 mt-3 md:mt-4">
                          Are you absolutely sure you want to proceed? This
                          action cannot be undone.
                        </p>
                      </div>

                      <DialogFooter className="gap-2">
                        <Button
                          variant="outline"
                          onClick={() => setIsDeleteModalOpen(false)}
                          className="border-slate-600 text-slate-300 hover:bg-slate-800 w-full sm:w-auto"
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={handleDeleteAccount}
                          disabled={deleteOrganizationMutation.isPending}
                          className="bg-red-600 hover:bg-red-700 w-full sm:w-auto"
                        >
                          {deleteOrganizationMutation.isPending
                            ? "Deleting..."
                            : "Yes, Delete Account"}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
